import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Input } from "@/components/uiDir/input";
import { Label } from "@/components/uiDir/label";
import { Button } from "@/components/uiDir/button";
import { Alert, AlertDescription } from "@/components/uiDir/alert";
import { sendPasswordResetEmail } from "@/lib/authService";
import { toast } from "sonner";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!email.trim()) {
      toast.error("Please enter an email address.");
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");

    try {
      const { error } = await sendPasswordResetEmail(email.trim());
      if (error) {
        toast.error(error.message);
        setStatusMessage(error.message);
        return;
      }

      const message =
        "Check your inbox for a password reset link. Be sure to complete the flow within the time limit.";
      toast.success(message);
      setStatusMessage("✅ " + message);
    } catch (err) {
      console.error("Password reset error:", err);
      const message = "Something went wrong while sending the reset link. Please try again.";
      toast.error(message);
      setStatusMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>

        <Card className="glass-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Forgot Password</CardTitle>
            <CardDescription>
              Enter the email linked to your PortfolioHub account and we’ll send you instructions to
              securely reset your password.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@ciit.edu.ph"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Only CIIT student addresses are supported during beta.
                </p>
              </div>

              {statusMessage && (
                <Alert className={statusMessage.startsWith("✅") ? "border-green-200 bg-green-50" : undefined}>
                  <AlertDescription className="text-sm">{statusMessage}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full flex items-center justify-center gap-2"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending reset link...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Remembered your password?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Log in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;

